import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Bug reports from students to their teacher (components/BugReport.tsx, and
// routes/teacher/BugReports.tsx). Rules: supabase/migrations/20261032_bug_reports.sql.
// Each report carries the page and a few details that help work out what
// went wrong: the browser, the screen size, and the last errors the page hit.
// ─────────────────────────────────────────────────────────────────────────────

export type BugReport = {
  id: string; classroom_id: string; student_id: string; page: string; message: string;
  details: Record<string, unknown>; status: 'open' | 'fixed'; created_at: string;
  /** The teacher's answer (20261037), and when they sent it. */
  reply?: string | null; replied_at?: string | null;
};

// The last few errors this page hit, kept to attach to a report.
const recent: { at: string; message: string }[] = [];
const remember = (message: string) => {
  recent.push({ at: new Date().toISOString(), message: message.slice(0, 300) });
  if (recent.length > 6) recent.shift();
};
export function watchErrors() {
  window.addEventListener('error', e => remember(String(e.message || e.error || 'error')));
  window.addEventListener('unhandledrejection', e => remember(String((e.reason as { message?: string })?.message ?? e.reason)));
}

function check<T>(res: { data: unknown; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

export async function sendBugReport(classroomId: string, studentId: string, message: string): Promise<void> {
  const details = {
    browser: navigator.userAgent,
    screen: `${window.innerWidth}×${window.innerHeight}`,
    touch: matchMedia('(pointer: coarse)').matches,
    errors: [...recent],
  };
  check(await supabase.from('bug_reports').insert({
    classroom_id: classroomId, student_id: studentId, message: message.trim(),
    page: (location.pathname + location.search).slice(0, 300), details,
  }));
}

export async function myBugReports(classroomId: string, studentId: string): Promise<BugReport[]> {
  return check(await supabase.from('bug_reports').select('*').eq('classroom_id', classroomId).eq('student_id', studentId)
    .order('created_at', { ascending: false }).limit(10));
}

// Teacher.
export async function classBugReports(classroomId: string): Promise<BugReport[]> {
  return check(await supabase.from('bug_reports').select('*').eq('classroom_id', classroomId).order('created_at', { ascending: false }).limit(200));
}
export async function openBugCount(classroomId: string): Promise<number> {
  const { count, error } = await supabase.from('bug_reports').select('id', { count: 'exact', head: true }).eq('classroom_id', classroomId).eq('status', 'open');
  if (error) return 0;
  return count ?? 0;
}
export async function setBugStatus(id: string, status: BugReport['status']): Promise<void> {
  check(await supabase.from('bug_reports').update({ status }).eq('id', id));
}
export async function deleteBugReport(id: string): Promise<void> {
  check(await supabase.from('bug_reports').delete().eq('id', id));
}

export async function replyToBug(id: string, reply: string): Promise<void> {
  const text = reply.trim();
  check(await supabase.from('bug_reports').update({ reply: text || null, replied_at: text ? new Date().toISOString() : null }).eq('id', id));
}

// Which replies a student has read, kept in their browser (just for the dot on the 🐞 tab).
const SEEN = 'bug-replies-seen';
export function seenReplies(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN) ?? '[]')); } catch { return new Set(); }
}
export function markRepliesSeen(keys: string[]) {
  try { const s = seenReplies(); keys.forEach(k => s.add(k)); localStorage.setItem(SEEN, JSON.stringify([...s].slice(-200))); } catch { /* fine */ }
}
/** A reply's key: the report and when it was replied to (so an edited reply counts as new). */
export const replyKey = (r: BugReport) => `${r.id}@${r.replied_at ?? ''}`;
